// Layout principal zona privada: AppBar + Drawer, i18n implementada

import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useTranslation } from "react-i18next";
import { resolveAvatarUrl } from "../utils/url";

import {
    AppBar, Avatar, Box, Divider, Drawer, IconButton, List,
    ListItemButton, ListItemIcon, ListItemText, Toolbar, Tooltip,
    Typography, Button, Menu, MenuItem, useTheme, useMediaQuery,
} from "@mui/material";

import MenuIcon           from "@mui/icons-material/Menu";
import DashboardIcon      from "@mui/icons-material/Dashboard";
import GroupsIcon         from "@mui/icons-material/Groups";
import ListAltIcon        from "@mui/icons-material/ListAlt";
import CompareArrowsIcon  from "@mui/icons-material/CompareArrows";
import ForumIcon          from "@mui/icons-material/Forum";
import PersonIcon         from "@mui/icons-material/Person";
import LogoutIcon         from "@mui/icons-material/Logout";
import ReceiptLongIcon    from "@mui/icons-material/ReceiptLong";
import StorefrontIcon     from "@mui/icons-material/Storefront";
import NotificationsIcon  from "@mui/icons-material/Notifications";
import HistoryIcon        from "@mui/icons-material/History";

import MobileBottomNavigation from "../components/MobileBottomNavigation";

const ANCHO_DRAWER = 260;

export default function LayoutPrivado({ usuario, foto, onLogout }) {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { t }     = useTranslation();
    const theme     = useTheme();

    /**
     * isMobile reactivo — se recalcula al rotar o redimensionar.
     */
    const isMobile  = useMediaQuery(theme.breakpoints.down("md"));
    const showHeader = !isMobile;

    const [drawerAbierto,    setDrawerAbierto]    = useState(false);
    const [anchorElPerfil,   setAnchorElPerfil]   = useState(null);

    const handleOpenPerfil  = (e) => setAnchorElPerfil(e.currentTarget);
    const handleClosePerfil = ()  => setAnchorElPerfil(null);

    const itemsMenu = useMemo(() => [
        { texto: t("menu.principal"), icono: <DashboardIcon />,    ruta: "/dashboard"  },
        { texto: t("menu.compare"),   icono: <CompareArrowsIcon />, ruta: "/compare"   },
        { texto: t("menu.stores"),    icono: <StorefrontIcon />,    ruta: "/tiendas"   },
        { texto: t("menu.rooms"),     icono: <GroupsIcon />,        ruta: "/salas"     },
        { texto: t("menu.chats"),     icono: <ForumIcon />,         ruta: "/mensajes"  },
        { texto: t("menu.lists"),     icono: <ListAltIcon />,       ruta: "/listas"    },
        { texto: t("menu.tickets"),   icono: <ReceiptLongIcon />,   ruta: "/admin/ocr" },
        { texto: t("menu.news"),      icono: <NotificationsIcon />, ruta: "/novedades" },
        { texto: t("menu.stats"),     icono: <HistoryIcon />,       ruta: "/historico" },
        { texto: t("menu.profile"),   icono: <PersonIcon />,        ruta: "/profile"   },
    ], [t]);

    /** Navega y cierra el drawer lateral */
    const irARuta = (ruta) => {
        navigate(ruta);
        setDrawerAbierto(false);
    };

    const contenidoDrawer = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar src={resolveAvatarUrl(foto)} alt={usuario || "Usuario"} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                        {usuario || "Usuario"}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>
                        Comprara
                    </Typography>
                </Box>
            </Box>

            <Divider />

            <List sx={{ p: 1 }}>
                {itemsMenu.map((item) => {
                    const seleccionado = location.pathname.startsWith(item.ruta);
                    return (
                        <ListItemButton
                            key={item.ruta}
                            selected={seleccionado}
                            onClick={() => irARuta(item.ruta)}
                            sx={{ borderRadius: 2, mb: 0.5 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>{item.icono}</ListItemIcon>
                            <ListItemText primary={item.texto} />
                        </ListItemButton>
                    );
                })}
            </List>

            <Box sx={{ mt: "auto", p: 2 }}>
                <Button fullWidth variant="outlined" startIcon={<LogoutIcon />} onClick={onLogout}>
                    {t("menu.logout")}
                </Button>
            </Box>
        </Box>
    );

    return (
        /*
         * Contenedor raíz con height 100dvh y overflow hidden.
         * Esto evita que la página "baile" o se desplace horizontalmente
         * en móvil. El scroll ocurre solo dentro del <main>.
         */
        <Box sx={{ display: "flex", height: "100dvh", overflow: "hidden" }}>

            {/* ── AppBar escritorio ────────────────────────────────────── */}
            {showHeader && (
                <AppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        borderBottom:   "1px solid rgba(0,0,0,0.06)",
                        bgcolor:        "white",
                        color:          "text.primary",
                        height:         64,
                        display:        "flex",
                        justifyContent: "center",
                    }}
                >
                    <Toolbar sx={{ gap: 1 }}>
                        {location.pathname !== "/dashboard" && location.pathname !== "/compare" && (
                            <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: "primary.main" }}>
                                <ArrowBackIosNewIcon />
                            </IconButton>
                        )}

                        <Typography variant="h6" sx={{ fontWeight: 900, flexGrow: 1, color: "primary.main" }}>
                            Comprara
                        </Typography>

                        <IconButton onClick={handleOpenPerfil} sx={{ p: 0.5 }}>
                            <Avatar
                                src={resolveAvatarUrl(foto)}
                                alt={usuario || "Usuario"}
                                sx={{ width: 36, height: 36, border: "2px solid", borderColor: "primary.light" }}
                            />
                        </IconButton>

                        <Menu
                            anchorEl={anchorElPerfil}
                            open={Boolean(anchorElPerfil)}
                            onClose={handleClosePerfil}
                            transformOrigin={{ horizontal: "right", vertical: "top"    }}
                            anchorOrigin={{   horizontal: "right", vertical: "bottom" }}
                            PaperProps={{ elevation: 4, sx: { mt: 1.5, minWidth: 180, borderRadius: 3 } }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{usuario || "Usuario"}</Typography>
                                <Typography variant="caption" color="text.secondary">Miembro de ComPrara</Typography>
                            </Box>
                            <Divider />
                            <MenuItem onClick={() => { navigate("/profile"); handleClosePerfil(); }}>
                                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={t("menu.profile")} />
                            </MenuItem>
                            <MenuItem onClick={() => { onLogout(); handleClosePerfil(); }}>
                                <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                                <ListItemText primary={t("menu.logout")} sx={{ color: "error.main" }} />
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>
            )}

            {/* ── Drawer lateral escritorio ────────────────────────────── */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", md: "block" },
                        width: ANCHO_DRAWER,
                        "& .MuiDrawer-paper": { width: ANCHO_DRAWER },
                    }}
                >
                    {contenidoDrawer}
                </Drawer>
            )}

            {/*
             * ── Contenido principal ──────────────────────────────────────
             * overflowY: "auto"  → scroll vertical solo aquí, no en toda la página
             * overflowX: "hidden" → evita scroll horizontal que hace "bailar" la UI
             * width: 100% + maxWidth garantizan que no se desborde
             */}
            <Box
                component="main"
                sx={{
                    flexGrow:   1,
                    overflowY:  "auto",
                    overflowX:  "hidden",
                    width:      "100%",
                    p:  isMobile ? 2 : 3,
                    pt: isMobile ? "calc(56px + env(safe-area-inset-top))"    : 10,
                    pb: isMobile ? "calc(64px + env(safe-area-inset-bottom))" : 3,
                    bgcolor:   isMobile ? "background.default" : "transparent",
                }}
            >
                <Outlet />
            </Box>

            {/* ── Bottom navigation móvil (siempre fija abajo) ─────────── */}
            <MobileBottomNavigation onLogout={onLogout} />
        </Box>
    );
}